/**
 * A pending action queued before an Auth0 redirect, and read back after it.
 *
 * Three separate flows need this — an invite-link seat claim, a self-enroll
 * course, a partner seat — and each had hand-rolled its own copy. That is how
 * the same bug got fixed three times: WPDS-137 fixed error reporting for the
 * partner path and left the other two, then the invite-link work fixed storage
 * for two paths and left the partner one. One implementation, three keys.
 *
 * Why localStorage: sessionStorage is per-TAB. Following a link from Outlook
 * frequently opens the app in a fresh tab, and corporate policy can force one,
 * in which case the queued value was simply gone by the time we could act on
 * it. The learner ended up with an account, no course, and no explanation.
 * localStorage also matches where the Auth0 SDK keeps its own cache
 * (cacheLocation="localstorage"), so if the value survives, so does the session
 * that needs it.
 */

/** Long enough to cover signup + email verification, short enough that an
 *  abandoned link doesn't re-fire weeks later on a shared machine. */
const TTL_MS = 24 * 60 * 60 * 1000;

type StoredValue = { token: string; setAt: string };

export interface PendingAction {
  remember: (value: string) => void;
  read: () => string | null;
  clear: () => void;
}

export function createPendingAction(key: string): PendingAction {
  const clear = () => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  };

  return {
    remember(value: string) {
      if (!value) return;
      const record: StoredValue = { token: value, setAt: new Date().toISOString() };
      try {
        localStorage.setItem(key, JSON.stringify(record));
      } catch {
        // Private browsing or a locked-down profile. Fall back to
        // sessionStorage: worse (dies with the tab) but better than dropping
        // the value entirely.
        try {
          sessionStorage.setItem(key, value);
        } catch {
          // Nothing writable. The learner can still act by revisiting the link
          // once signed in, which the landing pages handle.
        }
      }
    },

    read() {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          // Values written by an earlier build were bare strings, not JSON.
          // Anyone mid-flight across the deploy still gets their course.
          if (!raw.startsWith('{')) return raw;

          const record = JSON.parse(raw) as StoredValue;
          if (!record?.token) return null;
          if (Date.now() - new Date(record.setAt).getTime() > TTL_MS) {
            clear();
            return null;
          }
          return record.token;
        }
      } catch {
        // Unreadable or malformed — fall through to sessionStorage.
      }

      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },

    clear,
  };
}
